import React from 'react';
import { Tag, Typography } from 'antd';
import type { RouteOperationalCoordination } from './types';
import './coordinationImpactStrip.css';

const { Text } = Typography;

const SEVERITY_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'warning',
  HIGH: 'orange',
  CRITICAL: 'error'
};

export type OperationalImpactStripProps = {
  coordination: RouteOperationalCoordination;
  compact?: boolean;
};

export const OperationalImpactStrip: React.FC<OperationalImpactStripProps> = ({
  coordination,
  compact = false
}) => {
  const {
    impactSummary,
    highestImpactSeverity,
    vehicleChain,
    cascade,
    conflicts,
    resourceOccupancy,
    recommendations
  } = coordination;

  const hasMaterial =
    impactSummary ||
    vehicleChain.impactedNextCount > 0 ||
    cascade.hasCascadeRisk ||
    conflicts.hasConflict ||
    resourceOccupancy.items.length > 0;

  if (!hasMaterial) return null;

  const severity = highestImpactSeverity ?? 'MEDIUM';
  const stripClass = `coordination-impact-strip coordination-impact-strip--${severity.toLowerCase()}${compact ? ' coordination-impact-strip--compact' : ''}`;

  const bullets: string[] = [];
  if (vehicleChain.summary) bullets.push(vehicleChain.summary);
  if (coordination.driverImpact.summary) bullets.push(coordination.driverImpact.summary);
  for (const c of conflicts.conflicts.slice(0, 2)) {
    bullets.push(c.title);
  }
  for (const r of resourceOccupancy.items.slice(0, 2)) {
    bullets.push(r.title);
  }
  for (const item of cascade.items.slice(0, 2)) {
    bullets.push(`${item.title} → ${item.downstream}`);
  }

  const uniqueBullets = Array.from(new Set(bullets)).slice(0, 4);

  return (
    <section className={stripClass}>
      <div className="coordination-impact-strip__head">
        <Text type="secondary">Operational impact</Text>
        {highestImpactSeverity ? (
          <Tag color={SEVERITY_COLORS[highestImpactSeverity] ?? 'warning'}>
            {highestImpactSeverity === 'CRITICAL' ? 'Критично' : highestImpactSeverity === 'HIGH' ? 'Высокое' : 'Влияние'}
          </Tag>
        ) : null}
        {conflicts.hasConflict ? <Tag color="error">Конфликт</Tag> : null}
      </div>
      {impactSummary ? (
        <div className="coordination-impact-strip__summary">{impactSummary}</div>
      ) : null}
      {!compact && uniqueBullets.length > 0 ? (
        <ul>
          {uniqueBullets.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {!compact && recommendations.length > 0 ? (
        <div style={{ marginTop: 6, fontSize: 12 }}>
          <Text type="secondary">Координация: </Text>
          {recommendations.slice(0, 2).map((r) => r.title).join(' · ')}
        </div>
      ) : null}
    </section>
  );
};

export default OperationalImpactStrip;
